import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import PaiButton from '../../ui/base-components/pai-button';
import PaiDotLoader from '../../ui/base-components/pai-dot-loader';
import LinkedinProjectItem from './linkedin-project-item';
import { LinkedinProject } from './profile-generator.types';

interface Props {
  classificationProjectName: string;
  classificationProjects: LinkedinProject[];
  isSignificant: boolean | undefined;
  significanceTestLoading: boolean;

  handleTestSignificance(): void;

  significanceTested(): boolean;

  startAnalysis(project: LinkedinProject): () => void;

  handleRestartAnalysis(project: LinkedinProject): () => void;

  downloadOverallAnalysisResults(id: string): () => void;

  downloadPersonaListAnalysisResults(id: string): () => void;

  handleOpenResultsPreview(projectId: string): () => void;
}

const EvaluateClassificationProjects: React.FC<Props> = ({
  classificationProjectName,
  classificationProjects,
  isSignificant,
  significanceTestLoading,
  handleTestSignificance,
  significanceTested,
  startAnalysis,
  handleRestartAnalysis,
  downloadOverallAnalysisResults,
  downloadPersonaListAnalysisResults,
  handleOpenResultsPreview,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const evaluationPossible = () => {
    return classificationProjects.some((project) => project.analysisFinished);
  };

  return (
    <div className={classes.container}>
      <Typography className={classes.title}>{t('manageClassificationProject', { projectName: classificationProjectName })}</Typography>
      <Typography className={classes.text}>{t('personaGeneratorClassificationProjectsText')}</Typography>
      <Stack className={classes.projects} direction={'column'}>
        {classificationProjects.map((project, index) => (
          <LinkedinProjectItem
            key={project.id}
            project={project}
            isEven={index % 2 === 0}
            highlight={significanceTested() && !isSignificant}
            startAnalysis={startAnalysis}
            handleRestartAnalysis={handleRestartAnalysis}
            downloadOverallAnalysisResults={downloadOverallAnalysisResults}
            downloadPersonaListAnalysisResults={downloadPersonaListAnalysisResults}
            handleOpenResultsPreview={handleOpenResultsPreview}
          />
        ))}
      </Stack>
      <div className={classes.buttonContainer}>
        {!significanceTestLoading ? (
          <PaiButton text={t('evaluatePersona')} action={handleTestSignificance} disabled={!evaluationPossible()} />
        ) : (
          <PaiDotLoader />
        )}
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    width: '100%',
    border: `2px solid ${theme.palette.backgrounds.main}`,
    padding: '20px 16px',
    height: 'calc(100% - 39px - 16px - 20px)',
  },
  title: {
    fontSize: theme.typography.pxToRem(18),
    marginBottom: '16px',
  },
  text: {
    fontSize: theme.typography.pxToRem(13),
    marginBottom: '16px',
  },
  projects: {
    border: `2px solid ${theme.palette.backgrounds.main}`,
    maxHeight: '425px',
    overflow: 'auto',
    overflowX: 'hidden',
    [theme.breakpoints.down('xl')]: {
      maxHeight: '350px',
    },
    [theme.breakpoints.down('lg')]: {
      maxHeight: '425px',
    },
  },
  cellTitle: {
    fontSize: theme.typography.pxToRem(12),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '24px',
  },
}));

export default EvaluateClassificationProjects;
